import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';

let initialized = false;

function cell(text) {
  const td = document.createElement('td');
  td.textContent = text == null || text === '' ? '—' : String(text);
  return td;
}

function formatDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

function statusLabel(status) {
  return ({
    pending: '待处理',
    processing: '处理中',
    succeeded: '成功',
    failed: '等待重试',
    dead: '失败',
    ignored: '已忽略',
  })[status] || status || '未知';
}

async function retryInbox(id) {
  const response = await authFetch(`/api/admin/market-analysis/feishu/inbox/${id}/retry`, {
    method: 'POST',
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || '重试失败');
  showToast('已加入重试队列', 'success');
  await loadFeishuSyncCenter();
}

function renderInbox(rows) {
  const body = document.getElementById('feishu-sync-inbox-body');
  if (!body) return;
  body.replaceChildren(...(rows || []).map((row) => {
    const tr = document.createElement('tr');
    tr.append(
      cell(formatDate(row.created_at)),
      cell(`${row.table_id || '—'}\n${row.record_id || '—'}`),
      cell(statusLabel(row.status)),
      cell(row.attempt_count),
      cell(row.last_error || row.event_type),
    );
    const action = document.createElement('td');
    if (['failed', 'dead', 'ignored'].includes(row.status)) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-secondary btn-sm';
      button.textContent = '重试';
      button.addEventListener('click', () => retryInbox(row.id).catch((error) => {
        showToast(error.message || '重试失败', 'error');
      }));
      action.append(button);
    } else {
      action.textContent = '—';
    }
    tr.append(action);
    return tr;
  }));
}

function renderAudits(rows) {
  const body = document.getElementById('feishu-sync-audit-body');
  if (!body) return;
  body.replaceChildren(...(rows || []).map((row) => {
    const tr = document.createElement('tr');
    tr.append(
      cell(formatDate(row.created_at)),
      cell(row.direction === 'inbound' ? '飞书 → 系统' : '系统 → 飞书'),
      cell(row.action),
      cell(row.actor_name || '系统'),
      cell(row.application_id),
      cell(row.outcome === 'success' ? '成功' : '失败'),
    );
    return tr;
  }));
}

function renderSwitches(switches) {
  const state = switches || {};
  const map = {
    'feishu.longConnection.enabled': state.longConnectionEnabled,
    'feishu.outboxWorker.enabled': state.outboxWorkerEnabled,
  };
  document.querySelectorAll('.feishu-sync-toggle').forEach((checkbox) => {
    const key = checkbox.dataset.key;
    if (key in map) checkbox.checked = Boolean(map[key]);
  });
}

async function toggleSync(key, enabled) {
  const response = await authFetch('/api/admin/market-analysis/feishu/sync-toggle', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, enabled }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || '开关更新失败');
  showToast(enabled ? '已开启' : '已关闭', 'success');
}

export async function loadFeishuSyncCenter() {
  const response = await authFetch('/api/admin/market-analysis/feishu/sync-center');
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || '同步中心加载失败');
  const data = body.data || {};
  document.querySelectorAll('[data-sync-count]').forEach((element) => {
    const [scope, status] = element.dataset.syncCount.split('.');
    element.textContent = String(data.counts?.[scope]?.[status] || 0);
  });
  renderSwitches(data.switches);
  renderInbox(data.inbox);
  renderAudits(data.audits);
}

export function initFeishuSyncCenter() {
  if (initialized) return;
  initialized = true;
  registerViewLoader('admin-feishu-sync', () => loadFeishuSyncCenter().catch((error) => {
    showToast(error.message || '同步中心加载失败', 'error');
  }));
  document.addEventListener('change', (event) => {
    if (!event.target?.classList?.contains('feishu-sync-toggle')) return;
    const checkbox = event.target;
    const key = checkbox.dataset.key;
    const enabled = checkbox.checked;
    checkbox.disabled = true;
    toggleSync(key, enabled)
      .catch((error) => {
        checkbox.checked = !enabled;
        showToast(error.message || '开关更新失败', 'error');
      })
      .finally(() => { checkbox.disabled = false; });
  });
  document.addEventListener('click', (event) => {
    if (event.target?.id === 'feishu-sync-refresh') {
      loadFeishuSyncCenter().catch((error) => showToast(error.message || '刷新失败', 'error'));
      return;
    }
    if (event.target?.id !== 'feishu-baseline-import') return;
    const button = event.target;
    button.disabled = true;
    button.textContent = '正在导入…';
    authFetch('/api/admin/market-analysis/feishu/import-baseline', {
      method: 'POST',
      body: JSON.stringify({}),
    }).then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || '历史数据导入失败');
      const result = body.data || {};
      showToast(
        `历史数据导入完成：新增 ${result.imported || 0}，跳过 ${result.skipped || 0}，失败 ${result.failed || 0}`,
        result.failed ? 'error' : 'success',
      );
      await loadFeishuSyncCenter();
    }).catch((error) => {
      showToast(error.message || '历史数据导入失败', 'error');
    }).finally(() => {
      button.disabled = false;
      button.textContent = '导入当前历史数据';
    });
  });
}
